/**
 * RangeCalendar — сетка месяца с выбором диапазона: навигация по месяцам,
 * подсветка диапазона, кружки на концах. Клик по дню репортится наружу;
 * логику «первый/второй клик» держит PeriodPicker.
 */

import {type ComponentProps, useState} from 'react';
import dayjs from 'dayjs';
import {ChevronLeft, ChevronRight} from 'lucide-react';

import {cn} from '@lib/utils';

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

type RangeCalendarProps = {
  from: string;
  to: string;
  onPickDay: (iso: string) => void;
};

export const RangeCalendar = ({from, to, onPickDay}: RangeCalendarProps) => {
  const [view, setView] = useState(() => dayjs(to || from || undefined).startOf('month'));

  const offset = (view.day() + 6) % 7;
  const gridStart = view.startOf('month').subtract(offset, 'day');
  const cells = Array.from({length: offset + view.daysInMonth()}, (_, i) =>
    gridStart.add(i, 'day'),
  );
  const fromD = dayjs(from);
  const toD = dayjs(to);
  const title = view.format('MMMM YYYY');

  return (
    <div>
      <div className="mb-2.5 flex items-center justify-between">
        <NavBtn onClick={() => setView(view.subtract(1, 'month'))} aria-label="Прошлый месяц">
          <ChevronLeft className="size-4" strokeWidth={2} />
        </NavBtn>
        <span className="text-[13.5px] font-semibold capitalize">{title}</span>
        <NavBtn onClick={() => setView(view.add(1, 'month'))} aria-label="Следующий месяц">
          <ChevronRight className="size-4" strokeWidth={2} />
        </NavBtn>
      </div>

      <div className="grid grid-cols-7 text-center text-[10.5px] text-skr-faint">
        {WEEKDAYS.map((w) => (
          <span key={w} className="py-1">
            {w}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {cells.map((d, i) => {
          const inMonth = i >= offset;
          const isFrom = d.isSame(fromD, 'day');
          const isTo = d.isSame(toD, 'day');
          const inRange = (d.isAfter(fromD, 'day') && d.isBefore(toD, 'day')) || isFrom || isTo;
          const end = isFrom || isTo;
          return (
            <button
              key={i}
              onClick={() => onPickDay(d.format('YYYY-MM-DD'))}
              className={cn(
                'h-9 font-mono text-[13px]',
                inRange && 'bg-accent',
                isFrom && 'rounded-l-[10px]',
                isTo && 'rounded-r-[10px]',
              )}
            >
              <span
                className={cn(
                  'mx-auto grid size-9 place-items-center rounded-full',
                  end
                    ? 'bg-primary font-semibold text-primary-foreground'
                    : inMonth
                      ? 'text-foreground'
                      : 'text-skr-faint',
                )}
              >
                {d.date()}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const NavBtn = ({children, ...props}: ComponentProps<'button'>) => (
  <button
    {...props}
    className="grid size-[30px] place-items-center rounded-lg bg-secondary text-muted-foreground hover:bg-muted"
  >
    {children}
  </button>
);
